import React from 'react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const s = {
  page: {
    width: '297mm',
    minHeight: '210mm',
    padding: '10mm 12mm',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    fontSize: '9px',
    color: '#1a1a1a',
    lineHeight: '1.35',
    background: '#fff',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '3px solid #0f172a',
    paddingBottom: '8px',
    marginBottom: '10px',
  },
  companyName: { fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '2px' },
  companySubtitle: { fontSize: '8px', color: '#64748b', marginTop: '2px' },
  docTitle: { fontSize: '13px', fontWeight: 800, color: '#0f172a', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '1px' },
  docSubtitle: { fontSize: '10px', fontWeight: 600, color: '#334155', textAlign: 'right', marginTop: '2px' },
  docDate: { fontSize: '8px', color: '#64748b', textAlign: 'right', marginTop: '4px' },

  filterBlock: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    gap: '4px 10px',
    border: '1px solid #cbd5e1',
    borderRadius: '3px',
    padding: '6px 8px',
    marginBottom: '8px',
    background: '#f8fafc',
  },
  fItem: { display: 'flex', flexDirection: 'column' },
  fLbl: { fontSize: '7px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  fVal: { fontSize: '9px', fontWeight: 600, color: '#0f172a' },

  legend: {
    display: 'flex',
    gap: '14px',
    alignItems: 'center',
    padding: '4px 8px',
    marginBottom: '8px',
    fontSize: '8px',
    color: '#475569',
  },
  dot: (bg) => ({ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', background: bg, marginRight: '4px', verticalAlign: 'middle' }),

  productBlock: {
    border: '1px solid #cbd5e1',
    borderRadius: '3px',
    marginBottom: '8px',
    pageBreakInside: 'avoid',
  },
  productHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#0f172a',
    color: '#fff',
    padding: '5px 9px',
  },
  productCode: { fontFamily: 'monospace', fontWeight: 800, fontSize: '11px' },
  productName: { marginLeft: '10px', fontSize: '10px', opacity: 0.85 },
  productKpis: { display: 'flex', gap: '14px', fontSize: '9px' },
  kpi: { textAlign: 'center' },
  kpiLbl: { fontSize: '6.5px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 },
  kpiVal: { fontSize: '11px', fontWeight: 800 },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: '8.5px' },
  th: {
    background: '#e2e8f0',
    color: '#0f172a',
    padding: '4px 6px',
    textAlign: 'left',
    fontSize: '7.5px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    borderBottom: '1px solid #94a3b8',
  },
  td: { padding: '4px 6px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' },
  tdC: { textAlign: 'center' },
  tdR: { textAlign: 'right' },
  mono: { fontFamily: 'monospace' },
  badge: (bg, color) => ({ display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '7.5px', fontWeight: 700, background: bg, color, border: '1px solid rgba(0,0,0,0.08)' }),

  footer: {
    marginTop: '8px',
    paddingTop: '6px',
    borderTop: '1px solid #cbd5e1',
    fontSize: '7px',
    color: '#94a3b8',
    display: 'flex',
    justifyContent: 'space-between',
  },
};

const statusStyle = {
  completed: { label: 'Consegnato', bg: '#d1fae5', color: '#065f46' },
  on_time: { label: 'In consegna', bg: '#fef3c7', color: '#92400e' },
  delayed: { label: 'In ritardo', bg: '#fee2e2', color: '#991b1b' },
};

const statusLabel = (line) => {
  const s = statusStyle[line.delay_status] || statusStyle.on_time;
  const suffix = line.delay_status === 'delayed' && line.delay_days > 0 ? ` · ${line.delay_days}gg` : '';
  return { ...s, label: s.label + suffix };
};

const rowBg = (st) => st === 'delayed' ? 'rgba(254,226,226,0.3)' : st === 'completed' ? 'rgba(209,250,229,0.2)' : 'transparent';

const PrintableOrderMatrix = React.forwardRef(({ products, clientName, filters, meta }, ref) => {
  const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div ref={ref} style={s.page}>
      <style>{'@page { size: A4 landscape; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }'}</style>

      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.companyName}>EVOTECH</div>
          <div style={s.companySubtitle}>Precision Machining · Internal ERP</div>
        </div>
        <div>
          <div style={s.docTitle}>Vista Ordini per Prodotto</div>
          <div style={s.docSubtitle}>{clientName ? `Cliente: ${clientName}` : 'Tutti i clienti'}</div>
          <div style={s.docDate}>Generato il {today}</div>
        </div>
      </div>

      {/* Filters summary */}
      <div style={s.filterBlock}>
        <div style={s.fItem}>
          <span style={s.fLbl}>Cliente</span>
          <span style={s.fVal}>{filters.clientLabel || 'Tutti'}</span>
        </div>
        <div style={s.fItem}>
          <span style={s.fLbl}>Prodotto</span>
          <span style={s.fVal}>{filters.productLabel || 'Tutti'}</span>
        </div>
        <div style={s.fItem}>
          <span style={s.fLbl}>Consegna dal</span>
          <span style={s.fVal}>{filters.dateFrom ? fmtDate(filters.dateFrom) : '-'}</span>
        </div>
        <div style={s.fItem}>
          <span style={s.fLbl}>Consegna al</span>
          <span style={s.fVal}>{filters.dateTo ? fmtDate(filters.dateTo) : '-'}</span>
        </div>
        <div style={s.fItem}>
          <span style={s.fLbl}>Solo in ritardo</span>
          <span style={s.fVal}>{filters.delayedOnly ? 'Si' : 'No'}</span>
        </div>
      </div>

      {/* Legend */}
      <div style={s.legend}>
        <span style={{ fontWeight: 700, color: '#0f172a' }}>Legenda:</span>
        <span><span style={s.dot('#10b981')}></span>Consegnato (residuo = 0)</span>
        <span><span style={s.dot('#f59e0b')}></span>In consegna (residuo &gt; 0, entro data)</span>
        <span><span style={s.dot('#ef4444')}></span>In ritardo (residuo &gt; 0, oltre data)</span>
      </div>

      {/* Products */}
      {(!products || products.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>
          Nessun ordine trovato con i filtri selezionati
        </div>
      ) : products.map(p => (
        <div key={p.product_id} style={s.productBlock}>
          <div style={s.productHeader}>
            <div>
              <span style={s.productCode}>{p.product_code}</span>
              <span style={s.productName}>{p.product_name}</span>
            </div>
            <div style={s.productKpis}>
              <div style={s.kpi}>
                <div style={s.kpiLbl}>Ordinato</div>
                <div style={s.kpiVal}>{p.total_ordered}</div>
              </div>
              <div style={s.kpi}>
                <div style={s.kpiLbl}>Consegnato</div>
                <div style={{ ...s.kpiVal, color: '#6ee7b7' }}>{p.total_delivered}</div>
              </div>
              <div style={s.kpi}>
                <div style={s.kpiLbl}>Residuo</div>
                <div style={{ ...s.kpiVal, color: p.total_remaining > 0 ? '#fbbf24' : '#94a3b8' }}>{p.total_remaining}</div>
              </div>
              {p.total_overdelivery > 0 && (
                <div style={s.kpi}>
                  <div style={s.kpiLbl}>Sovracons.</div>
                  <div style={{ ...s.kpiVal, color: '#f59e0b' }}>+{p.total_overdelivery}</div>
                </div>
              )}
            </div>
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Ordine</th>
                <th style={s.th}>Cliente</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Qta Ord.</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Consegnato</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Residuo</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Sovracons.</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Consegna Rich.</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Ultima Consegna</th>
                <th style={{ ...s.th, textAlign: 'center' }}>Stato</th>
              </tr>
            </thead>
            <tbody>
              {p.order_lines.map(line => {
                const st = statusLabel(line);
                return (
                  <tr key={line.line_id} style={{ background: rowBg(line.delay_status) }}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 600 }}>
                      {line.order_number}
                      {line.customer_order_ref ? <span style={{ color: '#94a3b8', marginLeft: '4px', fontSize: '7px' }}>({line.customer_order_ref})</span> : null}
                    </td>
                    <td style={s.td}>{line.client_name || '-'}</td>
                    <td style={{ ...s.td, ...s.tdC, fontWeight: 600 }}>{line.quantity}</td>
                    <td style={{ ...s.td, ...s.tdC, color: line.overdelivery_quantity > 0 ? '#b45309' : '#047857', fontWeight: 700 }}>{line.delivered_quantity}</td>
                    <td style={{ ...s.td, ...s.tdC, color: line.remaining_quantity > 0 ? '#b45309' : '#94a3b8', fontWeight: 700 }}>{line.remaining_quantity}</td>
                    <td style={{ ...s.td, ...s.tdC, color: line.overdelivery_quantity > 0 ? '#b45309' : '#cbd5e1', fontWeight: 700 }}>{line.overdelivery_quantity > 0 ? `+${line.overdelivery_quantity}` : '—'}</td>
                    <td style={{ ...s.td, ...s.tdC, color: line.delay_status === 'delayed' ? '#b91c1c' : '#334155', fontWeight: line.delay_status === 'delayed' ? 600 : 400 }}>{fmtDate(line.delivery_date)}</td>
                    <td style={{ ...s.td, ...s.tdC, color: '#475569' }}>{fmtDate(line.last_delivery_date)}</td>
                    <td style={{ ...s.td, ...s.tdC }}>
                      <span style={s.badge(st.bg, st.color)}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <div style={s.footer}>
        <span>Documento generato automaticamente da Internal ERP · Evotech SRL</span>
        <span>{meta?.totalProducts || products?.length || 0} prodotti · {meta?.totalLines || products?.reduce((n, p) => n + p.order_lines.length, 0) || 0} righe ordine</span>
      </div>
    </div>
  );
});

PrintableOrderMatrix.displayName = 'PrintableOrderMatrix';
export default PrintableOrderMatrix;
